import Phaser from "phaser";

import { TROPHY_HALL_ART, WORKSHOP_ENERGY_ART } from "../config/artAssets";
import type { GameConfig } from "../config/gameConfig";
import {
  advanceRelicDuelMatch,
  autoSelectRelicDuelImpulse,
  createRelicDuelMatch,
  currentRelicDuelPot,
  resolveRelicDuelRound,
  type RelicDuelImpulse,
  type RelicDuelMatch,
  type RelicDuelRound,
} from "../domain/relicDuel";

type ArtAsset = { key: string; url: string; width: number; height: number };
type DuelScreen = "tables" | "intro" | "match";
type HapticKind = "selection" | "success" | "error";

export type RelicDuelCopy = {
  locale: "ru" | "en";
  kicker: string;
  title: string;
  subtitle: string;
  chooseTable: string;
  tableHint: string;
  previewNotice: string;
  available: string;
  carats: string;
  opponent: string;
  you: string;
  versus: string;
  round: string;
  roundPot: string;
  chooseImpulse: string;
  impulseHint: string;
  locked: string;
  autoMove: string;
  playerRoundWin: string;
  opponentRoundWin: string;
  tieRound: string;
  carryPot: string;
  resultWin: string;
  resultLoss: string;
  resultDraw: string;
  resultWinBody: string;
  resultLossBody: string;
  resultDrawBody: string;
  playAgain: string;
  chooseAnotherTable: string;
  strength: string;
  canvasLabel: string;
};

export type RelicDuelSceneCallbacks = {
  onReady?(): void;
  onAnnouncement?(message: string): void;
  onHaptic?(kind: HapticKind): void;
  onExitRequested?(): void;
};

type ImpulseToken = {
  container: Phaser.GameObjects.Container;
  value: Phaser.GameObjects.Text;
  face: Phaser.GameObjects.Graphics;
};

const RELIC_ASSETS: readonly ArtAsset[] = [
  WORKSHOP_ENERGY_ART.sigil,
  TROPHY_HALL_ART.achievements["living-facet"],
  TROPHY_HALL_ART.achievements["makers-circle"],
  TROPHY_HALL_ART.achievements["perfect-facet"],
  TROPHY_HALL_ART.achievements["arena-guardian"],
];

const COLORS = {
  deep: 0x041f2b,
  navy: 0x082e3a,
  teal: 0x0d6a6f,
  turquoise: 0x65e7dc,
  pale: 0xd8fff4,
  cream: 0xfff0cf,
  gold: 0xf2bd6f,
  copper: 0xb56e3e,
  danger: 0xea8d74,
} as const;

const RECTANGLE_CONTAINS: Phaser.Types.Input.HitAreaCallback = (hitArea, x, y) =>
  Phaser.Geom.Rectangle.Contains(hitArea as Phaser.Geom.Rectangle, x, y);

function formatInteger(value: string | number, locale: RelicDuelCopy["locale"]): string {
  return new Intl.NumberFormat(locale === "ru" ? "ru-RU" : "en-US", { maximumFractionDigits: 0 }).format(Number(value));
}

function relicAsset(value: RelicDuelImpulse): ArtAsset {
  const asset = RELIC_ASSETS[value - 1];
  if (asset === undefined) throw new Error(`Relic art is missing for value ${value}`);
  return asset;
}

export class RelicDuelScene extends Phaser.Scene {
  readonly #callbacks: RelicDuelSceneCallbacks;
  readonly #config: GameConfig;
  readonly #copy: RelicDuelCopy;
  readonly #availableBalance: string;
  readonly #renderScale: number;

  #reducedMotion: boolean;
  #screen: DuelScreen = "tables";
  #match: RelicDuelMatch | null = null;
  #selectedStake: string | null = null;
  #matchSequence = 0;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #inputLocked = false;
  #turnDeadlineAt = 0;
  #phaseTimer: Phaser.Time.TimerEvent | null = null;

  #background!: Phaser.GameObjects.Image;
  #shade!: Phaser.GameObjects.Rectangle;
  #floorFacet!: Phaser.GameObjects.Graphics;
  #ui!: Phaser.GameObjects.Container;
  #transients!: Phaser.GameObjects.Container;
  #timerTrack: Phaser.GameObjects.Graphics | null = null;
  #timerLabel: Phaser.GameObjects.Text | null = null;
  #playerScoreText: Phaser.GameObjects.Text | null = null;
  #opponentScoreText: Phaser.GameObjects.Text | null = null;
  #currentRelicImage: Phaser.GameObjects.Image | null = null;
  #currentRelicBaseY = 0;
  #cardCenters = new Map<RelicDuelImpulse, Phaser.Math.Vector2>();
  #ambientShards: Phaser.GameObjects.Polygon[] = [];

  constructor(
    callbacks: RelicDuelSceneCallbacks,
    config: GameConfig,
    copy: RelicDuelCopy,
    availableBalance: string,
    reducedMotion: boolean,
    renderScale: number,
  ) {
    super({ key: "RelicDuel" });
    this.#callbacks = callbacks;
    this.#config = config;
    this.#copy = copy;
    this.#availableBalance = availableBalance;
    this.#reducedMotion = reducedMotion;
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.image(TROPHY_HALL_ART.background.key, TROPHY_HALL_ART.background.url);
    for (const asset of RELIC_ASSETS) this.load.image(asset.key, asset.url);
  }

  create(): void {
    this.#background = this.add.image(0, 0, TROPHY_HALL_ART.background.key).setDepth(0);
    this.#shade = this.add.rectangle(0, 0, 10, 10, COLORS.deep, 0.46).setOrigin(0).setDepth(1);
    this.#floorFacet = this.add.graphics().setDepth(2);
    this.#ambientShards = Array.from({ length: 12 }, (_, index) =>
      this.add
        .polygon(0, 0, [0, -8, 5, 0, 0, 11, -5, 0], index % 3 === 0 ? COLORS.gold : COLORS.turquoise, 0.18)
        .setStrokeStyle(1, index % 3 === 0 ? 0xffe1a6 : 0xb8fff4, 0.3)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(3),
    );
    this.#ui = this.add.container(0, 0).setDepth(5);
    this.#transients = this.add.container(0, 0).setDepth(12);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.input.keyboard?.on("keydown", this.#handleKeyboard, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#callbacks.onReady?.();
    this.#announce(this.#copy.chooseTable);
  }

  override update(time: number): void {
    if (!this.#reducedMotion) {
      this.#ambientShards.forEach((shard, index) => {
        const phase = time * 0.00042 + index * 0.74;
        shard.y += Math.sin(phase) * 0.018;
        shard.setAlpha(0.11 + (Math.sin(phase * 1.17) + 1) * 0.065);
      });
      if (this.#currentRelicImage !== null && this.#match?.phase === "choosing") {
        this.#currentRelicImage.y = this.#currentRelicBaseY + Math.sin(time * 0.0018) * 2.4;
      }
    }

    if (this.#match?.phase !== "choosing" || this.#inputLocked || this.#turnDeadlineAt <= 0) return;
    const remaining = Math.max(0, this.#turnDeadlineAt - time);
    this.#drawTimer(remaining / this.#config.relicDuel.turnMs);
    if (this.#timerLabel !== null) this.#timerLabel.setText(String(Math.max(0, Math.ceil(remaining / 1_000))));
    if (remaining <= 0) {
      const impulse = autoSelectRelicDuelImpulse(this.#match, this.#config);
      this.#chooseImpulse(impulse, true);
    }
  }

  setReducedMotion(reducedMotion: boolean): void {
    if (this.#reducedMotion === reducedMotion) return;
    this.#reducedMotion = reducedMotion;
    if (this.sys.isActive()) this.#buildCurrentScreen();
  }

  debugSnapshot(): Record<string, unknown> {
    return {
      screen: this.#screen,
      availableBalance: this.#availableBalance,
      phase: this.#match?.phase ?? null,
      stake: this.#selectedStake,
      roundIndex: this.#match?.roundIndex ?? null,
      relicOrder: this.#match?.relicOrder ?? [],
      currentRelic: this.#match === null || this.#match.phase === "result"
        ? null
        : this.#match.relicOrder[this.#match.roundIndex] ?? null,
      currentPot: this.#match === null ? 0 : currentRelicDuelPot(this.#match),
      playerScore: this.#match?.playerScore ?? 0,
      opponentScore: this.#match?.opponentScore ?? 0,
      playerRemaining: this.#match?.playerRemaining ?? [],
      result: this.#match?.result ?? null,
      inputLocked: this.#inputLocked,
      width: this.#layoutWidth,
      height: this.#layoutHeight,
      cardCenters: Object.fromEntries([...this.#cardCenters].map(([value, point]) => [value, { x: point.x, y: point.y }])),
      tableCenters: this.#tableCenters(),
      reducedMotion: this.#reducedMotion,
    };
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);
    const backgroundScale = Math.max(width / TROPHY_HALL_ART.background.width, height / TROPHY_HALL_ART.background.height);
    this.#background.setPosition(width / 2, height / 2).setScale(backgroundScale);
    this.#shade.setSize(width, height);
    this.#drawFloorFacet();
    this.#layoutAmbientShards();

    if (this.#match?.phase === "reveal") {
      this.#phaseTimer?.remove(false);
      this.#phaseTimer = null;
      this.#match = advanceRelicDuelMatch(this.#match);
      this.#inputLocked = false;
    }
    this.#buildCurrentScreen();
  }

  #drawFloorFacet(): void {
    const width = this.#layoutWidth;
    const height = this.#layoutHeight;
    const centreX = width / 2;
    const centreY = height * 0.55;
    this.#floorFacet.clear();
    this.#floorFacet.fillStyle(COLORS.navy, 0.55);
    this.#floorFacet.fillPoints([
      new Phaser.Geom.Point(centreX, centreY - 150),
      new Phaser.Geom.Point(width - 22, centreY - 34),
      new Phaser.Geom.Point(width - 48, centreY + 160),
      new Phaser.Geom.Point(centreX, centreY + 205),
      new Phaser.Geom.Point(48, centreY + 160),
      new Phaser.Geom.Point(22, centreY - 34),
    ], true);
    this.#floorFacet.lineStyle(1.4, COLORS.copper, 0.62);
    this.#floorFacet.strokePoints([
      new Phaser.Geom.Point(centreX, centreY - 150),
      new Phaser.Geom.Point(width - 22, centreY - 34),
      new Phaser.Geom.Point(width - 48, centreY + 160),
      new Phaser.Geom.Point(centreX, centreY + 205),
      new Phaser.Geom.Point(48, centreY + 160),
      new Phaser.Geom.Point(22, centreY - 34),
    ], true);
    this.#floorFacet.lineStyle(1, COLORS.turquoise, 0.13);
    this.#floorFacet.lineBetween(centreX, centreY - 150, centreX, centreY + 205);
    this.#floorFacet.lineBetween(22, centreY - 34, width - 48, centreY + 160);
    this.#floorFacet.lineBetween(width - 22, centreY - 34, 48, centreY + 160);
  }

  #layoutAmbientShards(): void {
    const width = this.#layoutWidth;
    const height = this.#layoutHeight;
    this.#ambientShards.forEach((shard, index) => {
      const left = index % 2 === 0;
      const column = Math.floor(index / 2);
      shard
        .setPosition(left ? 16 + (column % 2) * 8 : width - 16 - (column % 2) * 8, 66 + column * ((height - 110) / 6))
        .setScale(0.55 + (index % 3) * 0.16)
        .setAngle(left ? -8 - index * 2 : 8 + index * 2);
    });
  }

  #buildCurrentScreen(): void {
    if (!this.sys.isActive() || this.#layoutWidth <= 0 || this.#layoutHeight <= 0) return;
    this.#phaseTimer?.remove(false);
    this.#phaseTimer = null;
    this.#ui.destroy(true);
    this.#transients.destroy(true);
    this.#ui = this.add.container(0, 0).setDepth(5);
    this.#transients = this.add.container(0, 0).setDepth(12);
    this.#timerTrack = null;
    this.#timerLabel = null;
    this.#playerScoreText = null;
    this.#opponentScoreText = null;
    this.#currentRelicImage = null;
    this.#cardCenters.clear();

    if (this.#screen === "tables") this.#buildTableSelection();
    else if (this.#screen === "intro") this.#buildIntro();
    else if (this.#match?.phase === "result") this.#buildResult();
    else this.#buildRound();
  }

  #buildTableSelection(): void {
    const width = this.#layoutWidth;
    const height = this.#layoutHeight;
    const compact = height < 610;
    const titleY = compact ? 31 : 38;
    this.#addText(width / 2, titleY, this.#copy.kicker, 9, "#f3bd70", "center", true);
    this.#addText(width / 2, titleY + 27, this.#copy.title, compact ? 27 : 32, "#fff1cf", "center", true, "Georgia");
    this.#addText(width / 2, titleY + 57, this.#copy.subtitle, compact ? 9 : 10, "#b9d9d3", "center", false, undefined, width - 54);

    const heroY = compact ? 138 : 166;
    const hero = this.#addRelicImage(5, width / 2, heroY, compact ? 90 : 112, this.#ui);
    this.#addFacetHalo(width / 2, heroY, compact ? 63 : 78, 0.19, this.#ui);
    this.#ui.bringToTop(hero);
    if (!this.#reducedMotion) {
      this.tweens.add({
        targets: hero,
        y: heroY - 5,
        duration: 1_900,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
    }

    const headingY = compact ? 207 : 245;
    this.#addText(width / 2, headingY, this.#copy.chooseTable, 14, "#fff1d1", "center", true, "Georgia");
    this.#addText(width / 2, headingY + 20, this.#copy.tableHint, 8.5, "#9fc5c0", "center", false, undefined, width - 50);

    const centers = this.#tableCenters();
    this.#config.relicDuel.stakes.forEach((stake, index) => {
      const centre = centers[index];
      if (centre === undefined) return;
      this.#makeButton(
        centre.x,
        centre.y,
        Math.min(151, width * 0.42),
        compact ? 54 : 61,
        formatInteger(stake, this.#copy.locale),
        this.#copy.carats,
        () => this.#startMatch(stake),
        index >= 2 ? "gold" : "teal",
      );
    });

    const noticeY = height - (compact ? 22 : 27);
    this.#addChamferedLabel(width / 2, noticeY, Math.min(width - 42, 300), 28, this.#copy.previewNotice);
  }

  #tableCenters(): { x: number; y: number }[] {
    const width = this.#layoutWidth;
    const compact = this.#layoutHeight < 610;
    const startY = compact ? 275 : 329;
    const gapY = compact ? 64 : 73;
    return [
      { x: width * 0.27, y: startY },
      { x: width * 0.73, y: startY },
      { x: width * 0.27, y: startY + gapY },
      { x: width * 0.73, y: startY + gapY },
    ];
  }

  #startMatch(stake: string): void {
    if (this.#inputLocked) return;
    this.#callbacks.onHaptic?.("selection");
    this.#selectedStake = stake;
    this.#matchSequence += 1;
    this.#match = createRelicDuelMatch(
      `relic-preview:${stake}:${this.#matchSequence}`,
      stake,
      this.#config,
    );
    this.#screen = "intro";
    this.#inputLocked = true;
    this.#turnDeadlineAt = 0;
    this.#buildCurrentScreen();
    this.#phaseTimer = this.time.delayedCall(this.#reducedMotion ? 300 : this.#config.relicDuel.introMs, () => {
      this.#screen = "match";
      this.#inputLocked = false;
      this.#buildCurrentScreen();
      this.#announce(this.#copy.chooseImpulse);
    });
  }

  #buildIntro(): void {
    const width = this.#layoutWidth;
    const height = this.#layoutHeight;
    const centreY = height * 0.44;
    this.#addText(width / 2, 42, `${formatInteger(this.#selectedStake ?? "0", this.#copy.locale)} ${this.#copy.carats}`, 10, "#f1bd73", "center", true);
    const left = this.#addRelicImage(2, width * 0.27, centreY, 96, this.#ui);
    const right = this.#addRelicImage(5, width * 0.73, centreY, 96, this.#ui);
    left.setAlpha(0.94);
    right.setAlpha(0.94);
    this.#addFacetHalo(left.x, left.y, 61, 0.14, this.#ui);
    this.#addFacetHalo(right.x, right.y, 61, 0.14, this.#ui);
    this.#ui.bringToTop(left);
    this.#ui.bringToTop(right);
    this.#addText(width * 0.27, centreY + 76, this.#copy.you, 10, "#c9fff4", "center", true);
    this.#addText(width * 0.73, centreY + 76, this.#copy.opponent, 10, "#ffe2ad", "center", true);
    const versus = this.#addText(width / 2, centreY, this.#copy.versus, 29, "#fff3d0", "center", true, "Georgia");
    versus.setStroke("#7c482e", 4).setShadow(0, 5, "#001820", 8, false, true);
    this.#addText(width / 2, height - 72, this.#copy.previewNotice, 8.5, "#9ec5bf", "center", false, undefined, width - 54);
    if (!this.#reducedMotion) {
      left.setX(-80);
      right.setX(width + 80);
      versus.setScale(0.55).setAlpha(0);
      this.tweens.add({ targets: left, x: width * 0.27, duration: 540, ease: "Back.easeOut" });
      this.tweens.add({ targets: right, x: width * 0.73, duration: 540, ease: "Back.easeOut" });
      this.tweens.add({ targets: versus, scale: 1, alpha: 1, delay: 260, duration: 330, ease: "Back.easeOut" });
    }
  }

  #buildRound(): void {
    const match = this.#match;
    if (match === null) return;
    const width = this.#layoutWidth;
    const height = this.#layoutHeight;
    const compact = height < 610;
    const currentRelic = match.relicOrder[match.roundIndex];
    if (currentRelic === undefined) return;

    this.#drawScoreboard(match);
    this.#drawRelicSequence(match);
    const heroY = compact ? height * 0.37 : height * 0.39;
    this.#currentRelicBaseY = heroY;
    this.#addFacetHalo(width / 2, heroY, compact ? 60 : 74, 0.18, this.#ui);
    this.#currentRelicImage = this.#addRelicImage(currentRelic, width / 2, heroY, compact ? 100 : 124, this.#ui);
    this.#addValueFacet(width / 2 + (compact ? 49 : 60), heroY + (compact ? 38 : 46), currentRelic, this.#ui);
    this.#addText(
      width / 2,
      heroY + (compact ? 75 : 91),
      `${this.#copy.roundPot}: ${currentRelicDuelPot(match)}`,
      10,
      "#ffd691",
      "center",
      true,
    );

    const promptY = height - (compact ? 126 : 145);
    this.#addText(width / 2, promptY, this.#copy.chooseImpulse, compact ? 13 : 15, "#fff0cf", "center", true, "Georgia");
    this.#addText(width / 2, promptY + 19, this.#copy.impulseHint, 8, "#9fc5bf", "center", false, undefined, width - 44);
    this.#timerTrack = this.add.graphics().setDepth(7);
    this.#ui.add(this.#timerTrack);
    this.#timerLabel = this.#addText(width / 2, promptY + 43, String(Math.ceil(this.#config.relicDuel.turnMs / 1_000)), 8, "#ffe0a5", "center", true);

    const values = this.#config.relicDuel.impulseValues;
    const cardGap = Math.min(61, (width - 34) / values.length);
    const cardY = height - (compact ? 42 : 49);
    values.forEach((value, index) => {
      const x = width / 2 + (index - 2) * cardGap;
      const available = match.playerRemaining.includes(value);
      this.#cardCenters.set(value, new Phaser.Math.Vector2(x, cardY));
      const token = this.#makeImpulseToken(x, cardY, value, available, false, this.#ui, compact ? 43 : 49);
      if (available) {
        token.container
          .setInteractive(new Phaser.Geom.Rectangle(-(compact ? 25 : 28), -(compact ? 31 : 35), compact ? 50 : 56, compact ? 62 : 70), RECTANGLE_CONTAINS)
          .on("pointerdown", () => this.#chooseImpulse(value, false))
          .on("pointerover", () => {
            if (!this.#inputLocked && !this.#reducedMotion) token.container.setY(cardY - 3);
          })
          .on("pointerout", () => token.container.setY(cardY));
        if (token.container.input !== null) token.container.input.cursor = "pointer";
      }
    });
    this.#startTurnTimer();
  }

  #drawScoreboard(match: RelicDuelMatch): void {
    const width = this.#layoutWidth;
    const topY = 28;
    this.#addScoreFacet(width * 0.2, topY, "player");
    this.#addScoreFacet(width * 0.8, topY, "opponent");
    this.#addText(width * 0.2, topY - 13, this.#copy.you, 7.5, "#9ceee5", "center", true);
    this.#playerScoreText = this.#addText(width * 0.2, topY + 7, String(match.playerScore), 20, "#fff2ce", "center", true, "Georgia");
    this.#addText(width * 0.8, topY - 13, this.#copy.opponent, 7.5, "#efbd7b", "center", true);
    this.#opponentScoreText = this.#addText(width * 0.8, topY + 7, String(match.opponentScore), 20, "#fff2ce", "center", true, "Georgia");
    this.#addText(
      width / 2,
      topY - 6,
      `${this.#copy.round} ${match.roundIndex + 1}/5`,
      9,
      "#ffd58f",
      "center",
      true,
    );
    this.#addText(width / 2, topY + 12, `${formatInteger(match.stake, this.#copy.locale)} ${this.#copy.carats}`, 7, "#8fb6b1", "center", true);
  }

  #drawRelicSequence(match: RelicDuelMatch): void {
    const width = this.#layoutWidth;
    const y = 77;
    const gap = Math.min(48, (width - 80) / 5);
    match.relicOrder.forEach((value, index) => {
      const x = width / 2 + (index - 2) * gap;
      const completed = index < match.roundIndex;
      const active = index === match.roundIndex;
      const frame = this.add.graphics();
      frame.fillStyle(active ? COLORS.teal : COLORS.navy, active ? 0.94 : 0.68);
      frame.lineStyle(active ? 2 : 1, active ? COLORS.gold : COLORS.turquoise, active ? 0.9 : 0.25);
      frame.fillPoints([
        new Phaser.Geom.Point(x, y - 15),
        new Phaser.Geom.Point(x + 14, y),
        new Phaser.Geom.Point(x, y + 15),
        new Phaser.Geom.Point(x - 14, y),
      ], true);
      frame.strokePoints([
        new Phaser.Geom.Point(x, y - 15),
        new Phaser.Geom.Point(x + 14, y),
        new Phaser.Geom.Point(x, y + 15),
        new Phaser.Geom.Point(x - 14, y),
      ], true);
      frame.setAlpha(completed ? 0.34 : 1);
      this.#ui.add(frame);
      this.#addText(x, y, String(value), 10, completed ? "#678c8b" : active ? "#fff0bd" : "#9ed6d1", "center", true);
    });
  }

  #startTurnTimer(): void {
    if (this.#match?.phase !== "choosing") return;
    this.#turnDeadlineAt = this.time.now + this.#config.relicDuel.turnMs;
    this.#drawTimer(1);
  }

  #drawTimer(ratio: number): void {
    if (this.#timerTrack === null || this.#timerLabel === null) return;
    const width = Math.min(172, this.#layoutWidth - 150);
    const x = this.#layoutWidth / 2 - width / 2;
    const y = this.#timerLabel.y + 11;
    const clamped = Phaser.Math.Clamp(ratio, 0, 1);
    this.#timerTrack.clear();
    this.#timerTrack.fillStyle(COLORS.deep, 0.72).fillRect(x, y, width, 3);
    this.#timerTrack.fillGradientStyle(COLORS.turquoise, COLORS.gold, COLORS.turquoise, COLORS.gold, 0.95);
    this.#timerTrack.fillRect(x, y, width * clamped, 3);
    this.#timerTrack.fillStyle(COLORS.gold, 0.95);
    this.#timerTrack.fillPoints([
      new Phaser.Geom.Point(x + width * clamped, y - 3),
      new Phaser.Geom.Point(x + width * clamped + 3, y + 1.5),
      new Phaser.Geom.Point(x + width * clamped, y + 6),
      new Phaser.Geom.Point(x + width * clamped - 3, y + 1.5),
    ], true);
  }

  #chooseImpulse(impulse: RelicDuelImpulse, automatic: boolean): void {
    const match = this.#match;
    if (
      match?.phase !== "choosing" ||
      this.#inputLocked ||
      !match.playerRemaining.includes(impulse)
    ) {
      return;
    }
    this.#inputLocked = true;
    this.#turnDeadlineAt = 0;
    this.#timerTrack?.clear();
    this.#timerLabel?.setText(automatic ? this.#copy.autoMove : this.#copy.locked);
    this.#callbacks.onHaptic?.("selection");
    const resolved = resolveRelicDuelRound(match, impulse, automatic);
    this.#match = resolved;
    this.#ui.iterate((child: Phaser.GameObjects.GameObject) => {
      const object = child as Phaser.GameObjects.GameObject & { disableInteractive?: () => void };
      object.disableInteractive?.();
      return true;
    });
    this.#playReveal(resolved.history.at(-1));
  }

  #playReveal(round: RelicDuelRound | undefined): void {
    if (round === undefined) return;
    const width = this.#layoutWidth;
    const height = this.#layoutHeight;
    const compact = height < 610;
    const centreY = this.#currentRelicBaseY + (compact ? 72 : 86);
    const playerStart = this.#cardCenters.get(round.playerImpulse) ?? new Phaser.Math.Vector2(width / 2, height - 45);
    const player = this.#makeImpulseToken(playerStart.x, playerStart.y, round.playerImpulse, true, false, this.#transients, compact ? 46 : 52);
    const opponent = this.#makeImpulseToken(width / 2, 106, round.opponentImpulse, true, true, this.#transients, compact ? 46 : 52);
    opponent.value.setText("?");
    player.container.setDepth(13);
    opponent.container.setDepth(13).setAlpha(0.78);
    const travelMs = this.#reducedMotion ? 90 : 430;
    this.tweens.add({
      targets: player.container,
      x: width / 2 - 34,
      y: centreY,
      angle: -8,
      duration: travelMs,
      ease: "Cubic.easeOut",
    });
    this.tweens.add({
      targets: opponent.container,
      x: width / 2 + 34,
      y: centreY,
      angle: 8,
      alpha: 1,
      duration: travelMs,
      ease: "Cubic.easeOut",
    });
    this.time.delayedCall(Math.max(70, travelMs - 55), () => {
      opponent.value.setText(String(round.opponentImpulse));
      this.#emitClash(width / 2, centreY, round);
      this.#playerScoreText?.setText(String(this.#match?.playerScore ?? 0));
      this.#opponentScoreText?.setText(String(this.#match?.opponentScore ?? 0));
      const message = round.winner === "player"
        ? this.#copy.playerRoundWin
        : round.winner === "opponent"
          ? this.#copy.opponentRoundWin
          : `${this.#copy.tieRound} ${this.#copy.carryPot}: ${round.pot}`;
      const color = round.winner === "player" ? "#baffef" : round.winner === "opponent" ? "#ffc8b0" : "#ffe0a0";
      const callout = this.#addText(width / 2, centreY + 57, message, compact ? 12 : 14, color, "center", true, "Georgia", width - 48, this.#transients);
      callout.setAlpha(0).setScale(0.8);
      this.tweens.add({ targets: callout, alpha: 1, scale: 1, duration: this.#reducedMotion ? 80 : 280, ease: "Back.easeOut" });
      this.#announce(message);
      this.#callbacks.onHaptic?.(round.winner === "player" ? "success" : round.winner === "opponent" ? "error" : "selection");
      this.#animateRelicAward(round);
    });

    const revealMs = this.#reducedMotion ? 620 : this.#config.relicDuel.revealMs;
    this.#phaseTimer = this.time.delayedCall(revealMs, () => {
      if (this.#match?.phase !== "reveal") return;
      this.#match = advanceRelicDuelMatch(this.#match);
      this.#inputLocked = false;
      this.#buildCurrentScreen();
      if (this.#match.phase === "choosing") this.#announce(this.#copy.chooseImpulse);
      else this.#announce(this.#resultTitle(this.#match));
    });
  }

  #emitClash(x: number, y: number, round: RelicDuelRound): void {
    const flash = this.add
      .polygon(x, y, [0, -38, 26, 0, 0, 38, -26, 0], round.winner === "tie" ? COLORS.gold : COLORS.turquoise, 0.72)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.#transients.add(flash);
    if (this.#reducedMotion) {
      flash.setAlpha(0.32).setScale(0.8);
      return;
    }
    flash.setScale(0.25);
    this.tweens.add({ targets: flash, scale: 1.35, alpha: 0, duration: 520, ease: "Quad.easeOut" });
    const particles = Array.from({ length: 14 }, (_, index) => {
      const color = index % 3 === 0 ? COLORS.gold : COLORS.turquoise;
      const shard = this.add.polygon(x, y, [0, -4, 2.4, 0, 0, 5, -2.4, 0], color, 0.95);
      this.#transients.add(shard);
      const angle = (Math.PI * 2 * index) / 14;
      const distance = 37 + (index % 4) * 9;
      this.tweens.add({
        targets: shard,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        angle: 90 + index * 21,
        alpha: 0,
        duration: 470 + (index % 3) * 80,
        ease: "Cubic.easeOut",
      });
      return shard;
    });
    void particles;
  }

  #animateRelicAward(round: RelicDuelRound): void {
    const relic = this.#currentRelicImage;
    if (relic === null) return;
    if (round.winner === "tie") {
      if (!this.#reducedMotion) {
        this.tweens.add({ targets: relic, scaleX: relic.scaleX * 1.08, scaleY: relic.scaleY * 1.08, duration: 260, yoyo: true, ease: "Sine.easeInOut" });
      }
      return;
    }
    const targetX = round.winner === "player" ? this.#layoutWidth * 0.2 : this.#layoutWidth * 0.8;
    const targetY = 33;
    if (this.#reducedMotion) {
      relic.setAlpha(0.45);
      return;
    }
    this.tweens.add({
      targets: relic,
      x: targetX,
      y: targetY,
      scaleX: relic.scaleX * 0.36,
      scaleY: relic.scaleY * 0.36,
      alpha: 0.18,
      duration: 620,
      delay: 120,
      ease: "Cubic.easeInOut",
    });
  }

  #buildResult(): void {
    const match = this.#match;
    if (match?.phase !== "result") return;
    const width = this.#layoutWidth;
    const height = this.#layoutHeight;
    const compact = height < 610;
    const title = this.#resultTitle(match);
    const body = match.result === "win"
      ? this.#copy.resultWinBody
      : match.result === "loss"
        ? this.#copy.resultLossBody
        : this.#copy.resultDrawBody;
    const heroValue: RelicDuelImpulse = match.result === "win" ? 4 : match.result === "loss" ? 5 : 3;
    const heroY = compact ? 135 : 165;
    this.#addText(width / 2, 29, `${formatInteger(match.stake, this.#copy.locale)} ${this.#copy.carats}`, 8, "#d6a968", "center", true);
    this.#addFacetHalo(width / 2, heroY, compact ? 68 : 82, match.result === "win" ? 0.26 : 0.14, this.#ui);
    const hero = this.#addRelicImage(heroValue, width / 2, heroY, compact ? 108 : 132, this.#ui);
    this.#addText(width / 2, heroY + (compact ? 83 : 99), title, compact ? 27 : 32, match.result === "loss" ? "#ffc0aa" : "#fff0c9", "center", true, "Georgia");
    this.#addText(width / 2, heroY + (compact ? 116 : 136), body, 9.5, "#b7d3ce", "center", false, undefined, width - 54);

    const scoreY = heroY + (compact ? 154 : 181);
    this.#addChamferedLabel(width / 2, scoreY, Math.min(width - 76, 244), 42, `${match.playerScore}  —  ${match.opponentScore}`);
    const actionsY = height - (compact ? 100 : 116);
    this.#makeButton(width / 2, actionsY, Math.min(width - 66, 268), 54, this.#copy.playAgain, `${formatInteger(match.stake, this.#copy.locale)} ${this.#copy.carats}`, () => this.#startMatch(match.stake), "gold");
    this.#makeButton(width / 2, actionsY + 65, Math.min(width - 66, 268), 45, this.#copy.chooseAnotherTable, "", () => {
      this.#callbacks.onHaptic?.("selection");
      this.#screen = "tables";
      this.#match = null;
      this.#selectedStake = null;
      this.#buildCurrentScreen();
      this.#announce(this.#copy.chooseTable);
    }, "teal");
    if (!this.#reducedMotion && match.result === "win") {
      hero.setScale(hero.scaleX * 0.82).setAlpha(0.35);
      this.tweens.add({ targets: hero, scaleX: hero.scaleX / 0.82, scaleY: hero.scaleY / 0.82, alpha: 1, duration: 520, ease: "Back.easeOut" });
      this.#emitClash(width / 2, heroY, match.history.at(-1) ?? {
        index: 4,
        relicValue: 5,
        pot: 5,
        playerImpulse: 5,
        opponentImpulse: 4,
        playerAuto: false,
        winner: "player",
        awardedPoints: 5,
      });
    }
  }

  #resultTitle(match: RelicDuelMatch): string {
    if (match.result === "win") return this.#copy.resultWin;
    if (match.result === "loss") return this.#copy.resultLoss;
    return this.#copy.resultDraw;
  }

  #makeImpulseToken(
    x: number,
    y: number,
    value: RelicDuelImpulse,
    available: boolean,
    opponent: boolean,
    parent: Phaser.GameObjects.Container,
    size: number,
  ): ImpulseToken {
    const container = this.add.container(x, y);
    const face = this.add.graphics();
    const halfWidth = size * 0.48;
    const halfHeight = size * 0.63;
    face.fillStyle(COLORS.deep, available ? 0.94 : 0.64);
    face.lineStyle(available ? 1.6 : 1, opponent ? COLORS.copper : COLORS.turquoise, available ? 0.9 : 0.24);
    const points = [
      new Phaser.Geom.Point(0, -halfHeight),
      new Phaser.Geom.Point(halfWidth, -halfHeight * 0.36),
      new Phaser.Geom.Point(halfWidth * 0.82, halfHeight * 0.68),
      new Phaser.Geom.Point(0, halfHeight),
      new Phaser.Geom.Point(-halfWidth * 0.82, halfHeight * 0.68),
      new Phaser.Geom.Point(-halfWidth, -halfHeight * 0.36),
    ];
    face.fillPoints(points, true).strokePoints(points, true);
    if (available) {
      face.fillGradientStyle(
        opponent ? 0x8c4f36 : 0x14666d,
        opponent ? 0x4f2d2b : 0x0b3c49,
        0x071f2c,
        0x071f2c,
        0.72,
      );
      face.fillPoints(points.map((point) => new Phaser.Geom.Point(point.x * 0.82, point.y * 0.84)), true);
      face.lineStyle(1, COLORS.gold, 0.25).lineBetween(0, -halfHeight * 0.78, 0, halfHeight * 0.72);
    }
    const label = this.add
      .text(0, -2, available ? String(value) : "×", {
        fontFamily: "Georgia, Times New Roman, serif",
        fontSize: `${Math.round(size * 0.43)}px`,
        fontStyle: "bold",
        color: available ? (opponent ? "#ffe0ab" : "#d9fff5") : "#496d70",
        align: "center",
      })
      .setOrigin(0.5);
    const strength = this.add
      .text(0, halfHeight * 0.58, available ? this.#copy.strength : "", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${Math.max(5, Math.round(size * 0.105))}px`,
        fontStyle: "bold",
        color: opponent ? "#d39f6e" : "#6ebcb7",
      })
      .setOrigin(0.5)
      .setAlpha(0.9);
    container.add([face, label, strength]);
    container.setAlpha(available ? 1 : 0.42);
    parent.add(container);
    return { container, value: label, face };
  }

  #makeButton(
    x: number,
    y: number,
    width: number,
    height: number,
    title: string,
    subtitle: string,
    action: () => void,
    tone: "teal" | "gold",
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const shadow = this.add.graphics();
    this.#drawChamferedPanel(shadow, width, height, COLORS.deep, COLORS.deep, 0.58, 4);
    const panel = this.add.graphics();
    this.#drawChamferedPanel(
      panel,
      width,
      height,
      tone === "gold" ? 0x5b3b2c : COLORS.navy,
      tone === "gold" ? COLORS.gold : COLORS.turquoise,
      0.94,
      0,
    );
    const startsWithDigit = /^\d/.exec(title) !== null;
    const titleText = this.add
      .text(0, subtitle.length > 0 ? -7 : 0, title, {
        fontFamily: startsWithDigit ? "Arial, sans-serif" : "Georgia, Times New Roman, serif",
        fontSize: `${startsWithDigit ? 19 : 14}px`,
        fontStyle: "bold",
        color: tone === "gold" ? "#fff0c9" : "#dffff7",
        align: "center",
      })
      .setOrigin(0.5);
    const subtitleText = this.add
      .text(0, 14, subtitle, {
        fontFamily: "Arial, sans-serif",
        fontSize: "7px",
        fontStyle: "bold",
        color: tone === "gold" ? "#e6b978" : "#78b9b6",
      })
      .setOrigin(0.5);
    container.add([shadow, panel, titleText, subtitleText]);
    container
      .setSize(width, height)
      .setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), RECTANGLE_CONTAINS)
      .on("pointerdown", () => {
        if (!this.#inputLocked) action();
      })
      .on("pointerover", () => {
        if (!this.#inputLocked && !this.#reducedMotion) container.setScale(1.025);
      })
      .on("pointerout", () => container.setScale(1));
    if (container.input !== null) container.input.cursor = "pointer";
    this.#ui.add(container);
    return container;
  }

  #drawChamferedPanel(
    graphics: Phaser.GameObjects.Graphics,
    width: number,
    height: number,
    fill: number,
    stroke: number,
    alpha: number,
    offsetY: number,
  ): void {
    const cut = Math.min(12, height * 0.25);
    const points = [
      new Phaser.Geom.Point(-width / 2 + cut, -height / 2 + offsetY),
      new Phaser.Geom.Point(width / 2, -height / 2 + offsetY),
      new Phaser.Geom.Point(width / 2, height / 2 - cut + offsetY),
      new Phaser.Geom.Point(width / 2 - cut, height / 2 + offsetY),
      new Phaser.Geom.Point(-width / 2, height / 2 + offsetY),
      new Phaser.Geom.Point(-width / 2, -height / 2 + cut + offsetY),
    ];
    graphics.fillStyle(fill, alpha).fillPoints(points, true);
    graphics.lineStyle(1.2, stroke, offsetY === 0 ? 0.72 : 0.18).strokePoints(points, true);
  }

  #addChamferedLabel(x: number, y: number, width: number, height: number, label: string): void {
    const panel = this.add.graphics();
    this.#drawChamferedPanel(panel, width, height, COLORS.deep, COLORS.copper, 0.78, 0);
    this.#ui.add(panel);
    panel.setPosition(x, y);
    this.#addText(x, y, label, 8, "#e8c796", "center", true);
  }

  #addScoreFacet(x: number, y: number, side: "player" | "opponent"): void {
    const panel = this.add.graphics();
    panel.fillStyle(COLORS.deep, 0.72);
    panel.lineStyle(1, side === "player" ? COLORS.turquoise : COLORS.copper, 0.52);
    const points = [
      new Phaser.Geom.Point(x, y - 26),
      new Phaser.Geom.Point(x + 39, y - 13),
      new Phaser.Geom.Point(x + 33, y + 25),
      new Phaser.Geom.Point(x, y + 31),
      new Phaser.Geom.Point(x - 33, y + 25),
      new Phaser.Geom.Point(x - 39, y - 13),
    ];
    panel.fillPoints(points, true).strokePoints(points, true);
    this.#ui.add(panel);
  }

  #addFacetHalo(
    x: number,
    y: number,
    radius: number,
    alpha: number,
    parent: Phaser.GameObjects.Container,
  ): Phaser.GameObjects.Polygon {
    const halo = this.add
      .polygon(x, y, [0, -radius, radius * 0.68, -radius * 0.28, radius * 0.82, radius * 0.54, 0, radius, -radius * 0.82, radius * 0.54, -radius * 0.68, -radius * 0.28], COLORS.turquoise, alpha)
      .setStrokeStyle(1.2, COLORS.gold, 0.36)
      .setBlendMode(Phaser.BlendModes.ADD);
    parent.add(halo);
    if (!this.#reducedMotion) {
      this.tweens.add({ targets: halo, angle: 5, alpha: alpha * 0.68, duration: 2_600, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });
    }
    return halo;
  }

  #addRelicImage(
    value: RelicDuelImpulse,
    x: number,
    y: number,
    maxHeight: number,
    parent: Phaser.GameObjects.Container,
  ): Phaser.GameObjects.Image {
    const asset = relicAsset(value);
    const image = this.add.image(x, y, asset.key);
    image.setScale(Math.min(maxHeight / asset.height, (maxHeight * 1.08) / asset.width));
    parent.add(image);
    return image;
  }

  #addValueFacet(
    x: number,
    y: number,
    value: RelicDuelImpulse,
    parent: Phaser.GameObjects.Container,
  ): void {
    const facet = this.add
      .polygon(x, y, [0, -18, 16, -4, 11, 15, -11, 15, -16, -4], COLORS.navy, 0.96)
      .setStrokeStyle(1.5, COLORS.gold, 0.9);
    const label = this.add
      .text(x, y, String(value), {
        fontFamily: "Georgia, Times New Roman, serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#fff1c3",
      })
      .setOrigin(0.5);
    parent.add([facet, label]);
  }

  #addText(
    x: number,
    y: number,
    value: string,
    fontSize: number,
    color: string,
    align: "left" | "center" | "right",
    bold: boolean,
    fontFamily = "Arial, sans-serif",
    wrapWidth?: number,
    parent: Phaser.GameObjects.Container = this.#ui,
  ): Phaser.GameObjects.Text {
    const text = this.add
      .text(x, y, value, {
        fontFamily,
        fontSize: `${fontSize}px`,
        fontStyle: bold ? "bold" : "normal",
        color,
        align,
        lineSpacing: 2,
        ...(wrapWidth === undefined ? {} : { wordWrap: { width: wrapWidth, useAdvancedWrap: true } }),
      })
      .setOrigin(align === "left" ? 0 : align === "right" ? 1 : 0.5, 0.5);
    parent.add(text);
    return text;
  }

  #handleKeyboard(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.#callbacks.onExitRequested?.();
      return;
    }
    const numeric = Number(event.key);
    if (!Number.isSafeInteger(numeric)) return;
    if (this.#screen === "tables" && numeric >= 1 && numeric <= this.#config.relicDuel.stakes.length) {
      const stake = this.#config.relicDuel.stakes[numeric - 1];
      if (stake !== undefined) this.#startMatch(stake);
      return;
    }
    if (this.#screen === "match" && numeric >= 1 && numeric <= 5) {
      this.#chooseImpulse(numeric as RelicDuelImpulse, false);
    }
  }

  #announce(message: string): void {
    this.#callbacks.onAnnouncement?.(message);
  }
}
